import React, { useCallback, useState } from 'react';
import { Image } from 'antd';
import { MailOutlined, WechatWorkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import lumeMoonIcon from '../../assets/images/lume-moon-icon.png';
import beianIcon from '../../assets/images/beian.png';
import wecomQrCodeZh from '../../assets/images/wecom_qrcode_zh.jpg';
import wecomQrCodeEn from '../../assets/images/wecom_qrcode_en.png';
import captureZh from '../../assets/images/lume-promo/capture-zh.png';
import captureEn from '../../assets/images/lume-promo/capture-en.png';
import sensoryZh from '../../assets/images/lume-promo/sensory-zh.png';
import sensoryEn from '../../assets/images/lume-promo/sensory-en.png';
import jarsZh from '../../assets/images/lume-promo/jars-zh.png';
import jarsEn from '../../assets/images/lume-promo/jars-en.png';
import analogZh from '../../assets/images/lume-promo/analog-zh.png';
import analogEn from '../../assets/images/lume-promo/analog-en.png';
import sanctuaryZh from '../../assets/images/lume-promo/sanctuary-zh.png';
import sanctuaryEn from '../../assets/images/lume-promo/sanctuary-en.png';
import { ReactComponent as PointSensoryIcon } from '../../assets/icons/app-lume/point-sensory.svg';
import { ReactComponent as PointEditingIcon } from '../../assets/icons/app-lume/point-editing.svg';
import { ReactComponent as PointJarsIcon } from '../../assets/icons/app-lume/point-jars.svg';
import { ReactComponent as PointTranscribeIcon } from '../../assets/icons/app-lume/point-transcribe.svg';
import { ReactComponent as PointLayoutIcon } from '../../assets/icons/app-lume/point-layout.svg';
import { ReactComponent as PointPrivateIcon } from '../../assets/icons/app-lume/point-private.svg';
import { ReactComponent as PointPeacefulIcon } from '../../assets/icons/app-lume/point-peaceful.svg';
import 'antd/dist/reset.css';
import './AppLume.scss';

const PROMO_IMAGES = {
  zh: {
    capture: captureZh,
    sensory: sensoryZh,
    jars: jarsZh,
    analog: analogZh,
    sanctuary: sanctuaryZh,
  },
  en: {
    capture: captureEn,
    sensory: sensoryEn,
    jars: jarsEn,
    analog: analogEn,
    sanctuary: sanctuaryEn,
  },
};

const WECOM_QR_CODES = {
  zh: wecomQrCodeZh,
  en: wecomQrCodeEn,
};

const FEATURE_BLOCKS = [
  {
    id: 'immersive',
    icon: '🌿',
    imageKeys: ['sensory', 'jars'],
    pointKeys: ['sensory', 'editing', 'jars'],
  },
  {
    id: 'analog',
    icon: '✍️',
    imageKeys: ['analog'],
    pointKeys: ['transcribe', 'layout'],
    reversed: true,
  },
  {
    id: 'sanctuary',
    icon: '✨',
    imageKeys: ['sanctuary'],
    pointKeys: ['private', 'peaceful'],
  },
];

const POINT_ICONS = {
  sensory: PointSensoryIcon,
  editing: PointEditingIcon,
  jars: PointJarsIcon,
  transcribe: PointTranscribeIcon,
  layout: PointLayoutIcon,
  private: PointPrivateIcon,
  peaceful: PointPeacefulIcon,
};

function BrandMark({ className = '', name = '月见半夏' }) {
  return (
    <span className={`app-lume-brand ${className}`}>
      <img src={lumeMoonIcon} alt="" className="app-lume-brand__icon" />
      <span className="app-lume-brand__name">{name}</span>
    </span>
  );
}

function FeatureBlock({ block, images, t }) {
  return (
    <section id={block.id === 'immersive' ? 'features' : undefined} className={`lume-feature ${block.reversed ? 'lume-feature--reversed' : ''}`}>
      <div className={`lume-feature__visual lume-feature__visual--${block.id}`}>
        {block.imageKeys.map((imageKey, index) => (
          <div key={imageKey} className={`lume-feature__image-card lume-feature__image-card--${index + 1}`}>
            <img
              src={images[imageKey]}
              alt={t(`lumePage.featureImages.${imageKey}`)}
              className="lume-feature__image"
            />
          </div>
        ))}
      </div>
      <div className="lume-feature__content">
        <div className="lume-feature__icon" aria-hidden="true">{block.icon}</div>
        <h2 className="lume-feature__title">{t(`lumePage.features.${block.id}.title`)}</h2>
        <p className="lume-feature__body">{t(`lumePage.features.${block.id}.body`)}</p>
        <div className="lume-feature__points">
          {block.pointKeys.map((pointKey) => {
            const PointIcon = POINT_ICONS[pointKey];

            return (
              <div key={pointKey} className={`lume-feature__point lume-feature__point--${pointKey}`}>
                <span className="lume-feature__point-icon" aria-hidden="true">
                  <PointIcon />
                </span>
                <h3 className="lume-feature__point-title">{t(`lumePage.features.${block.id}.points.${pointKey}.title`)}</h3>
                <p className="lume-feature__point-desc">{t(`lumePage.features.${block.id}.points.${pointKey}.desc`)}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AppOenothera() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language?.startsWith('zh');
  const images = PROMO_IMAGES[isZh ? 'zh' : 'en'];
  const wecomQrCode = WECOM_QR_CODES[isZh ? 'zh' : 'en'];
  const brandName = isZh ? '月见半夏' : 'Oenothera';
  const currentYear = new Date().getFullYear();
  const [showWecomQr, setShowWecomQr] = useState(false);

  const toggleLanguage = useCallback(() => {
    const next = i18n.language?.startsWith('zh') ? 'en' : 'zh';
    i18n.changeLanguage(next);
  }, [i18n]);

  const openWecomQr = useCallback(() => {
    setShowWecomQr(true);
  }, []);

  const closeWecomQr = useCallback(() => {
    setShowWecomQr(false);
  }, []);

  return (
    <div className="app-lume">
      <header className="app-lume-nav">
        <a href="/" className="app-lume-nav__brand" aria-label={t('lumePage.nav.homeAria')}>
          <BrandMark name={brandName} />
        </a>
        <nav className="app-lume-nav__links" aria-label={t('lumePage.nav.aria')}>
          <a href="#features" className="app-lume-nav__link">{t('lumePage.nav.features')}</a>
          <a href="#philosophy" className="app-lume-nav__link">{t('lumePage.nav.philosophy')}</a>
          <a href="#download" className="app-lume-nav__link">{t('lumePage.nav.download')}</a>
        </nav>
        <div className="app-lume-nav__actions">
          <a href="#contact" className="app-lume-nav__contact">{t('lumePage.nav.contact')}</a>
          <button
            type="button"
            className="app-lume-nav__lang"
            onClick={toggleLanguage}
            aria-label={t('lumePage.nav.languageAria')}
          >
            {isZh ? 'EN' : '中'}
          </button>
        </div>
      </header>

      <main className="app-lume__main">
        <section className="app-lume-hero">
          <div className="app-lume-hero__copy">
            <h1 className="app-lume-hero__title" aria-label={t('lumePage.hero.slogan')}>
              <span>{t('lumePage.hero.titleLead')}</span>
              {!isZh && ' '}
              <span className="app-lume-hero__title-accent">{t('lumePage.hero.titleAccent')}</span>
            </h1>
            <p className="app-lume-hero__subtitle">{t('lumePage.hero.subtitle')}</p>
            <div className="app-lume-hero__actions">
              <a href="https://apps.apple.com/cn/app/id6761891999" target="_blank" rel="noopener noreferrer" className="app-lume-btn app-lume-btn--primary">{t('lumePage.hero.primaryBtn')}</a>
              <a href="#philosophy" className="app-lume-btn app-lume-btn--secondary">{t('lumePage.hero.secondaryBtn')}</a>
            </div>
          </div>
          <div className="app-lume-hero__visual">
            <div className="app-lume-hero__phone-card">
              <img src={images.capture} alt={t('lumePage.featureImages.capture')} />
            </div>
          </div>
        </section>

        <section id="philosophy" className="app-lume-philosophy">
          <h2 className="app-lume-philosophy__quote">
            <span>{t('lumePage.philosophy.quoteLine1')}</span>
            <span>{t('lumePage.philosophy.quoteLine2')}</span>
          </h2>
          <p className="app-lume-philosophy__body">{t('lumePage.philosophy.body')}</p>
        </section>

        <div className="app-lume-features">
          {FEATURE_BLOCKS.map((block) => (
            <FeatureBlock key={block.id} block={block} images={images} t={t} />
          ))}
        </div>

        <section id="download" className="app-lume-download">
          <div className="app-lume-download__image-card">
            <img src={images.capture} alt={t('lumePage.featureImages.capture')} />
          </div>
          <div className="app-lume-download__copy">
            <h2 className="app-lume-download__title">
              <span>{t('lumePage.download.titleLine1')}</span>
              <span>{t('lumePage.download.titleLine2')}</span>
            </h2>
            <p className="app-lume-download__desc">{t('lumePage.download.desc')}</p>
            <a href="https://apps.apple.com/cn/app/id6761891999" target="_blank" rel="noopener noreferrer" className="app-lume-btn app-lume-btn--primary app-lume-download__btn">
              {t('lumePage.download.button')}
            </a>
          </div>
        </section>

        <section id="contact" className="app-lume-contact">
          <div className="app-lume-contact__icon" aria-hidden="true">✉️</div>
          <h2 className="app-lume-contact__title">{t('lumePage.contact.title')}</h2>
          <p className="app-lume-contact__desc">{t('lumePage.contact.desc')}</p>
          <div className="app-lume-contact__actions">
            <a href="mailto:support@unnamedlight.com" className="app-lume-contact__email">
              <MailOutlined className="app-lume-contact__email-icon" />
              support@unnamedlight.com
            </a>
            <button type="button" className="app-lume-contact__wecom" onClick={openWecomQr}>
              <WechatWorkOutlined className="app-lume-contact__wecom-icon" />
              {t('lumePage.contact.wecom')}
            </button>
          </div>
        </section>
      </main>

      <footer className="app-lume-footer">
        <div className="app-lume-footer__divider" />
        <div className="app-lume-footer__row">
          <BrandMark className="app-lume-footer__brand" name={brandName} />
          <nav className="app-lume-footer__links" aria-label={t('lumePage.footer.aria')}>
            <a href="/lume/terms/privacy">{t('lumePage.footer.privacy')}</a>
            <a href="/lume/terms/user">{t('lumePage.footer.terms')}</a>
            <a href="#contact">{t('lumePage.footer.contact')}</a>
          </nav>
        </div>
        <div className="app-lume-footer__legal">
          <p className="app-lume-footer__copyright">© 2025-{currentYear} {t('footer.copyright')}</p>
          <div className="app-lume-footer__filing">
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">粤ICP备2025433423号-3</a>
            <a href="https://beian.mps.gov.cn/#/query/webSearch?code=44010402003632" target="_blank" rel="noopener noreferrer">
              <img src={beianIcon} alt="" className="app-lume-footer__beian-icon" />
              粤公网安备44010402003632号
            </a>
          </div>
        </div>
      </footer>

      {showWecomQr && (
        <div className="app-lume-qr-modal" role="presentation" onClick={closeWecomQr}>
          <div
            className="app-lume-qr-modal__card"
            role="dialog"
            aria-modal="true"
            aria-label={t('lumePage.contact.wecom')}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="app-lume-qr-modal__close"
              onClick={closeWecomQr}
              aria-label={t('lumePage.contact.qrClose')}
            >
              ×
            </button>
            <Image
              src={wecomQrCode}
              alt={t('lumePage.contact.qrAlt')}
              className="app-lume-qr-modal__image"
              preview={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AppOenothera;
